import axiosInstance from 'src/config/axios.config';
import { isAxiosError } from 'axios';

import type {
  QrCodeMetadata,
  QrCodeGenerationRequest,
  QrCodeGenerationResponse,
  InvalidateQrCodeRequest,
} from '../types/qr-codes';
import { getUsernameFromToken } from '../utils/jwt';

/**
 * QR Codes API Module
 * Handles all API operations related to QR code generation, history, and invalidation
 */

// Base endpoint for QR codes
const QR_CODES_ENDPOINT = '/api/mobile/qr-code';
const QR_CODES_PLURAL_ENDPOINT = '/api/mobile/qr-codes';

/**
 * Generate a new QR code for a branch
 * @param branchId - Branch ID to generate QR code for
 * @param request - QR code generation parameters (ttlHours, locationRangeMeters)
 * @returns Promise with generated QR code metadata
 */
/**
 * Backend response from QR code list endpoint
 */
interface QrCodeListItem {
  id: string;
  branchId: string;
  encryptedPayload: string;
  generatedAt: string;
  expiresAt: string | null;
  generatedBy: string;
  locationRangeMeters: number;
  isActive: boolean;
  isExpired: boolean;
}

/**
 * Get all active QR codes for a branch
 * @param branchId - Branch ID to fetch QR codes for
 * @returns Promise with array of QR code list items
 */
export const getQrCodesForBranch = async (
  branchId: string
): Promise<QrCodeListItem[]> => {
  try {
    const response = await axiosInstance.get<QrCodeListItem[]>(
      `${QR_CODES_PLURAL_ENDPOINT}/${branchId}`
    );
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      if (error.response?.status === 401) {
        throw new Error('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.');
      }
      if (error.response?.status === 403) {
        throw new Error('Bu işlem için yetkiniz bulunmuyor.');
      }
    }
    throw new Error('QR kodları yüklenirken hata oluştu');
  }
};

export const generateQrCode = async (
  branchId: string,
  request: QrCodeGenerationRequest
): Promise<QrCodeMetadata> => {
  try {
    const response = await axiosInstance.get<QrCodeGenerationResponse>(
      `${QR_CODES_ENDPOINT}/${branchId}`,
      {
        params: {
          ttlHours: request.ttlHours,
          locationRange: request.locationRangeMeters,
        },
      }
    );

    // Map backend response to frontend type
    const backendData = response.data;
    const currentUser = getUsernameFromToken();

    // Calculate if expired
    const isExpired = backendData.expiresAt
      ? new Date(backendData.expiresAt) < new Date()
      : false;

    const mappedData: QrCodeMetadata = {
      id: backendData.branchId, // Use branchId as temporary ID
      branchId: backendData.branchId,
      branchName: backendData.branchName,
      branchAddress: '', // Will be filled from branch data in dialog
      latitude: 0, // Will be filled from branch data in dialog
      longitude: 0, // Will be filled from branch data in dialog
      locationRangeMeters: backendData.locationRangeMeters,
      encryptedQrCode: backendData.encryptedQrCode,
      generatedAt: backendData.generatedAt,
      expiresAt: backendData.expiresAt || undefined,
      ttlHours: backendData.ttlHours,
      isExpired,
      generatedBy: currentUser || 'unknown',
      generatedByName: currentUser || 'Unknown',
      invalidated: false,
      invalidatedBy: undefined,
      invalidatedAt: undefined,
    };

    return mappedData;
  } catch (error) {
    if (isAxiosError(error)) {
      if (error.response?.status === 401) {
        throw new Error('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.');
      }
      if (error.response?.status === 403) {
        throw new Error('Bu işlem için yetkiniz bulunmuyor.');
      }
      if (error.response?.status === 404) {
        throw new Error('Şube bulunamadı.');
      }
      if (error.response?.status === 400) {
        throw new Error(
          error.response?.data?.message ||
            'Geçersiz QR kod oluşturma isteği.'
        );
      }
      throw new Error(
        error.response?.data?.message ||
          'QR kod oluşturulurken hata oluştu'
      );
    }
    throw new Error('QR kod oluşturulurken hata oluştu');
  }
};

